import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { FinalCTA } from "@/components/home/FinalCTA";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  organizationSchema,
  localBusinessSchema,
  generateBreadcrumbSchema,
} from "@/lib/structuredData";
import { servicePages, existingServiceLinks, type ServicePageData } from "@/data/servicePages";

const BASE_URL = "https://bookkeepingservices.co.za";
const WHATSAPP_URL =
  "https://wa.me/27732509394?text=" +
  encodeURIComponent("Hi! I'd like to chat to an accountant about my business.");

export function ServicePageTemplate({ data }: { data: ServicePageData }) {
  const canonicalPath = `/${data.slug}`;
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.h1,
    description: data.metaDescription,
    areaServed: "ZA",
    provider: {
      "@type": "Organization",
      name: "Bookkeeping Services South Africa",
      url: BASE_URL,
    },
    url: `${BASE_URL}${canonicalPath}`,
  };

  const relatedServices = [
    ...servicePages
      .filter((s) => s.slug !== data.slug)
      .map((s) => ({ slug: s.slug, label: s.eyebrow })),
    ...existingServiceLinks.map((s) => ({ slug: s.slug, label: s.eyebrow })),
  ];

  const pillarMap: Record<string, { href: string; label: string; description: string }> = {
    "monthly-bookkeeping-services": {
      href: "/bookkeeping",
      label: "Read the full Bookkeeping in South Africa guide",
      description: "Complete guide to bookkeeping in SA — what it costs, how it's done, and how to use it to run a better business.",
    },
    "payroll-emp201-emp501": {
      href: "/bookkeeping",
      label: "Read the Bookkeeping guide",
      description: "How payroll fits into a properly-run set of books in South Africa.",
    },
    "uif-monthly-declarations": {
      href: "/bookkeeping",
      label: "Read the Bookkeeping guide",
      description: "How UIF fits into monthly payroll and bookkeeping in South Africa.",
    },
    "annual-tax-returns": {
      href: "/vat",
      label: "Read the VAT in South Africa guide",
      description: "Understand how VAT interacts with company tax and provisional tax filings.",
    },
    "provisional-tax-returns": {
      href: "/vat",
      label: "Read the VAT in South Africa guide",
      description: "How provisional tax, VAT and company tax fit together for SA businesses.",
    },
    "cipc-annual-returns": {
      href: "/cipc",
      label: "Read the CIPC Compliance guide",
      description: "Everything South African companies need to know about CIPC — annual returns, beneficial ownership and restoration.",
    },
    "beneficial-ownership-submission": {
      href: "/cipc",
      label: "Read the CIPC Compliance guide",
      description: "Beneficial ownership rules, deadlines and penalties in South Africa.",
    },
    "workmens-compensation": {
      href: "/bookkeeping",
      label: "Read the Bookkeeping guide",
      description: "How COIDA fits into ongoing payroll and compliance for SA employers.",
    },
  };
  const pillar = pillarMap[data.slug];

  return (
    <Layout>
      <SEO
        title={data.seoTitle}
        description={data.metaDescription}
        canonical={canonicalPath}
        structuredData={[
          organizationSchema,
          localBusinessSchema,
          serviceSchema,
          faqSchema,
          generateBreadcrumbSchema([
            { name: "Home", url: `${BASE_URL}/` },
            { name: "Services", url: `${BASE_URL}/services` },
            { name: data.eyebrow, url: `${BASE_URL}${canonicalPath}` },
          ]),
        ]}
      />

      <Breadcrumbs
        items={[{ name: "Services", href: "/services" }, { name: data.eyebrow }]}
      />

      {/* Hero */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/30" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-medium text-primary mb-4 tracking-wide uppercase">
              {data.eyebrow}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              {data.h1}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
              {data.intro}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-a-call">
                <Button size="lg" className="btn-primary gap-2 text-base px-8 h-14">
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  variant="outline"
                  className="btn-outline-primary gap-2 text-base px-8 h-14"
                >
                  <MessageCircle className="w-5 h-5" />
                  Chat to an Accountant
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Long-form content */}
      <section className="py-20 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-10">
            {data.sections.map((s) => (
              <article key={s.heading}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
                  {s.heading}
                </h2>
                <p className="text-muted-foreground leading-relaxed text-base">{s.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pillar hub CTA — topic cluster internal link */}
      {pillar && (
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-xs uppercase tracking-wide text-primary font-medium mb-2">Learn more</p>
                <p className="text-foreground font-heading text-lg md:text-xl font-semibold mb-1">{pillar.label}</p>
                <p className="text-sm text-muted-foreground">{pillar.description}</p>
              </div>
              <Link to={pillar.href} className="shrink-0">
                <Button variant="outline" className="btn-outline-primary gap-2">
                  Open guide <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Bullets */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="What's Included"
            title={`${data.eyebrow} at a glance`}
          />
          <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
            {data.bullets.map((b) => (
              <div
                key={b}
                className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border"
              >
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="FAQ"
            title="Frequently asked questions"
            description={`Common questions about ${data.eyebrow.toLowerCase()} in South Africa.`}
          />
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {data.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="bg-card border border-border rounded-xl px-6 hover:border-primary/20 transition-colors data-[state=open]:border-primary/30 data-[state=open]:shadow-md"
                >
                  <AccordionTrigger className="text-left font-heading text-base font-semibold hover:no-underline hover:text-primary transition-colors py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Related services / internal linking */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <SectionHeader
            label="Related Services"
            title="Explore our other services"
            description="Bookkeeping, payroll, tax and CIPC compliance — all under one SAICA registered team."
          />
          <div className="max-w-4xl mx-auto flex flex-wrap gap-3 justify-center">
            {relatedServices.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                className="px-4 py-2 rounded-full border border-border bg-card text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
              >
                {r.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
}
